import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { trpc } from '@/utils/trpc'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { motion } from 'framer-motion'
import { CreditCard, User, Mail, AlertCircle, Heart, Search } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY, {
  locale: 'sv',
})

export const Route = createFileRoute('/')({
  component: () => (
    <Elements stripe={stripePromise}>
      <Index />
    </Elements>
  ),
})

function Index() {
  const [amount, setAmount] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()

  const checkPaymentIntent = useMutation(trpc.donation.checkPaymentIntent.mutationOptions({
    onSuccess: async (data) => {
      // Check if the payment was successful
      if (data.status) {
        location.href = `/subscription/${encodeURIComponent(data.subscriptionId)}`
        return
      } else {
        setError('Betalningen misslyckades, var god försök igen senare')
        return
      }
    },
    onError: (error) => {
      setError(error.message)
    }
  }))

  const createSubscription = useMutation(trpc.donation.createSubscription.mutationOptions({
    onSuccess: async (data) => {
      // Ensure stripe is initialized
      if (!stripe) {
        setError('Stripe är inte initialiserat')
        return
      }

      // Check if 3D Secure authentication is required
      if (data.requiresAction) {
        // Ensure the client secret is provided
        if (!data.clientSecret) {
          setError('Saknar klienthemlighet')
          return
        }

        // Perform 3D Secure authentication
        const { paymentIntent, error: paymentIntentError } = await stripe.confirmCardPayment(data.clientSecret)
        if (paymentIntentError || !paymentIntent) {
          setError('Betalningen misslyckades, var god försök igen senare')
          return
        }

        // Check if the payment succeeded
        if (paymentIntent.status === 'succeeded') {
          location.href = `/subscription/${encodeURIComponent(data.subscriptionId)}`
          return
        }

        // Run second check if payment was successful
        checkPaymentIntent.mutate({ id: data.id, subscriptionId: data.subscriptionId })
      } else {
        location.href = `/subscription/${encodeURIComponent(data.subscriptionId)}`
        return
      }
    },
    onError: (error) => {
      setError(error.message)
    },
  }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Check all inputs
    if (!amount || !email || !name) {
      setError('Vänligen fyll i alla fält')
      return
    }

    if (!stripe || !elements) {
      setError('Stripe är inte initialiserat')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setError('Kortelement hittades inte')
      return
    }

    // Create Stripe payment method
    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (paymentMethodError || !paymentMethod) {
      setError('Kunde inte skapa betalningsmetod')
      return
    }

    // Send request to create subscription
    createSubscription.mutate({
      amount: parseInt(amount),
      email,
      name,
      paymentMethodId: paymentMethod.id
    })
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto max-w-2xl p-6"
    >
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
        >
          <Heart className="w-20 h-20 text-green-500 mx-auto mb-4" />
        </motion.div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">KRIS Stödförening</h1>
        <p className="text-gray-600">Stöd vårt arbete med en månadsvis gåva</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-6 mb-8"
      >
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/subscriptions' })}
          className="w-full mb-6 hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition-colors"
        >
          <Search className="w-4 h-4 mr-2" />
          Hitta Mina Gåvor
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-gray-50 rounded-xl">
              <div className="w-6 h-6 font-semibold text-green-500 mr-4">SEK</div>
              <div className="flex-1">
                <Label htmlFor="amount" className="text-sm text-gray-600">Månadsbelopp</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Ange månadsbelopp"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-xl">
              <User className="w-6 h-6 text-green-500 mr-4" />
              <div className="flex-1">
                <Label htmlFor="name" className="text-sm text-gray-600">Namn</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ange ditt namn"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-xl">
              <Mail className="w-6 h-6 text-green-500 mr-4" />
              <div className="flex-1">
                <Label htmlFor="email" className="text-sm text-gray-600">E-post</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ange din e-post"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-xl">
              <CreditCard className="w-6 h-6 text-green-500 mr-4" />
              <div className="flex-1">
                <Label className="text-sm text-gray-600 mb-2 block">Kortinformation</Label>
                <div className="p-3 border rounded-md bg-white">
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center p-4 bg-red-50 text-red-800 rounded-xl"
            >
              <AlertCircle className="w-5 h-5 mr-2" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}

          <Button
            type="submit"
            className="w-full py-6 text-lg hover:bg-green-600 transition-colors"
            disabled={createSubscription.isPending}
          >
            {createSubscription.isPending ? 'Bearbetar...' : 'Starta Månadsvis Gåva'}
          </Button>

          <div className="flex justify-center">
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
              <img src="https://images.stripeassets.com/fzn2n1nzq965/6XFEUA9FzMBMphYdcUab19/37a1e07201366a351f7956560ccac09d/Stripe_wordmark_-_slate.svg" alt="Stripe" className="w-16" />
            </a>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}